import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import subscriptionPaymentService from "../../api/subscriptionPaymentService";

// ✅ EXISTING THUNK - UNCHANGED
export const subscriptionPayment = createAsyncThunk(
  "subscriptionPayment/subscriptionPayment",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const response = await subscriptionPaymentService.subscriptionPayment(
        payload,
      );

      return {
        subscriptionPaymentItems: response?.data || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ✅ EXISTING THUNK - UNCHANGED
export const subscriptionSuccess = createAsyncThunk(
  "subscriptionPayment/subscriptionSuccess",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await subscriptionPaymentService.subscriptionSuccess(
        payload,
      );

      return {
        subscriptionSuccess: response?.data || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// 🆕 NEW THUNK - Preview upgrade cost
export const previewUpgrade = createAsyncThunk(
  "subscriptionPayment/previewUpgrade",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await subscriptionPaymentService.SubscriptionPreview(
        payload,
      );

      return response?.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getTransectionDetails = createAsyncThunk(
  "/frontend/transaction-details",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await subscriptionPaymentService.getTransectionDetails(
        payload,
      );

      return response?.data || {};
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  "subscriptionPayment/cancleSubscription",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await subscriptionPaymentService.cancelSubscription(
        payload,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to cancel subscription",
      );
    }
  },
);

const initialState = {
  // ✅ EXISTING STATE - UNCHANGED
  subscriptionPaymentItems: {},
  subscriptionSuccessItems: {},
  cancleSubscriptionItems: {},
  transectionDetails: {},
  isLoading: false,
  transectionDetailsLoading: false,
  isError: false,
  errorMessage: "",

  // 🆕 NEW STATE - For preview functionality
  previewData: null,
  isPreviewLoading: false,
  previewError: null,
  isUpgradeSuccess: false,
};

const subscriptionPaymentSlice = createSlice({
  name: "subscriptionPayment",
  initialState,
  reducers: {
    // 🆕 NEW REDUCERS - For clearing preview state
    clearPreview: (state) => {
      state.previewData = null;
      state.previewError = null;
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
      state.previewError = null;
    },
    resetUpgradeSuccess: (state) => {
      state.isUpgradeSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ EXISTING CASES - UNCHANGED
      .addCase(subscriptionPayment.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(subscriptionPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptionPaymentItems =
          action.payload.subscriptionPaymentItems || {};

        // 🆕 NEW: Check if upgrade was processed immediately
        if (action.payload.subscriptionPaymentItems?.charged_immediately) {
          state.isUpgradeSuccess = true;
        }
      })
      .addCase(subscriptionPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload || "Failed to fetch subscription Payment.";
        state.actionFlag = "GET_SUBSCRIPTION_PAYMENT_FAILED";
      })

      // ✅ EXISTING CASES - UNCHANGED
      .addCase(subscriptionSuccess.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(subscriptionSuccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptionSuccessItems =
          action.payload.subscriptionSuccess || "";
      })
      .addCase(subscriptionSuccess.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload || "Failed to success payment";
        state.actionFlag = "PAYMENT_SUCCESS_FAILED";
      })

      // 🆕 NEW CASES - Preview upgrade
      .addCase(previewUpgrade.pending, (state) => {
        state.isPreviewLoading = true;
        state.previewError = null;
      })
      .addCase(previewUpgrade.fulfilled, (state, action) => {
        state.isPreviewLoading = false;
        state.previewData = action.payload;
      })
      .addCase(previewUpgrade.rejected, (state, action) => {
        state.isPreviewLoading = false;
        state.previewError =
          action.payload?.error || "Failed to preview upgrade";
      })
      // New thunk
      // 🆕 NEW CASES
      .addCase(getTransectionDetails.pending, (state) => {
        state.isLoading = true;
        state.transectionDetailsLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(getTransectionDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transectionDetailsLoading = false;
        state.transectionDetails = action.payload;
      })
      .addCase(getTransectionDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.transectionDetailsLoading = false;
        state.isError = true;
        state.errorMessage =
          action.payload || "Failed to get transaction-details";
      })
      .addCase(cancelSubscription.pending, (state) => {
        state.isPreviewLoading = true;
        state.previewError = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isPreviewLoading = false;
        state.cancleSubscriptionItems = action.payload;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isPreviewLoading = false;
        state.previewError =
          action.payload?.error || "Failed to preview upgrade";
      });
  },
});

export const {
  clearPreview,
  clearError,
  resetUpgradeSuccess,
} = subscriptionPaymentSlice.actions;
export default subscriptionPaymentSlice.reducer;
